import React from 'react';
import { CommentButton } from './Comments';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';

export function KpiCard({ label, value, sub, accent = false, testId, commentTarget, trend, sparkline }) {
  return (
    <div
      data-testid={testId}
      className={`${accent ? 'bg-indie-cream border-t-4 border-indie-evergreen' : 'bg-white border-t-4 border-indie-navy'} p-6 flex flex-col gap-2 relative group`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="label-editorial">{label}</div>
        {commentTarget && (
          <span className="no-print opacity-40 group-hover:opacity-100 transition-opacity">
            <CommentButton targetType="kpi" targetId={commentTarget.id} label={commentTarget.label || label} scenarioId={commentTarget.scenarioId} />
          </span>
        )}
      </div>
      <div className="text-3xl lg:text-4xl font-semibold text-indie-navy num-tabular tracking-tight">{value}</div>
      {sub && <div className="text-sm text-indie-ink/70">{sub}</div>}
      {(trend || sparkline) && (
        <div className="mt-2 pt-2 border-t border-indie-navy/10 flex items-center justify-between gap-3">
          {trend && <TrendIndicator {...trend} />}
          {sparkline && sparkline.data && sparkline.data.length > 1 && (
            <div className="h-8 w-24 flex-shrink-0" data-testid={`${testId}-sparkline`}>
              <ResponsiveContainer>
                <LineChart data={sparkline.data} margin={{ top: 2, bottom: 2, left: 0, right: 0 }}>
                  <Line type="monotone" dataKey="v" stroke={sparkline.color || '#1D57A5'} strokeWidth={1.5} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TrendIndicator({ deltaPct, label, positiveIsGood = true }) {
  if (deltaPct == null || Number.isNaN(deltaPct)) {
    return <div className="text-[11px] text-indie-ink/40 uppercase tracking-wider">{label} · n/a</div>;
  }
  const flat = Math.abs(deltaPct) < 0.005;
  const positive = deltaPct >= 0;
  const good = flat ? null : (positive === positiveIsGood);
  const color = flat ? 'text-indie-ink/50' : good ? 'text-indie-evergreen' : 'text-indie-neg-dark';
  const Icon = flat ? Minus : positive ? ArrowUpRight : ArrowDownRight;
  return (
    <div className={`flex items-center gap-1 text-[11px] font-medium ${color}`}>
      <Icon size={12} />
      <span className="num-tabular">{positive && !flat ? '+' : ''}{(deltaPct * 100).toFixed(1)}%</span>
      <span className="text-indie-ink/50 normal-case tracking-normal font-normal">· {label}</span>
    </div>
  );
}

export function SectionHeader({ eyebrow, title, description, right }) {
  return (
    <div className="mb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
      <div>
        {eyebrow && <div className="label-editorial mb-2 text-indie-cobalt">{eyebrow}</div>}
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-indie-navy">{title}</h1>
        {description && <p className="mt-2 text-base text-indie-ink/70 max-w-2xl leading-relaxed">{description}</p>}
      </div>
      {right}
    </div>
  );
}

export function Callout({ title, children, tone = 'default' }) {
  // tone: default=evergreen · cta=yellow (use for must-see calls-to-action) · alert=red · success=evergreen
  const border = tone === 'alert' ? 'border-indie-neg'
    : tone === 'cta' ? 'border-indie-yellow'
    : 'border-indie-evergreen';
  return (
    <div className={`bg-indie-cream border-t-4 ${border} p-6`}>
      {title && <div className="label-editorial mb-2">{title}</div>}
      <div className="text-indie-ink/90 leading-relaxed">{children}</div>
    </div>
  );
}

export function NavyBand({ children, className = '' }) {
  return (
    <div className={`-mx-6 sm:-mx-8 lg:-mx-12 ${className}`}>
      <div className="bg-indie-navy text-indie-cream px-6 sm:px-8 lg:px-12 py-8">{children}</div>
      <div className="h-[3px] bg-indie-yellow" />
    </div>
  );
}
